import {
  Container,
  Graphics,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type Ticker,
  type Texture
} from 'pixi.js';
import { BaseScene, type SceneServices } from './BaseScene';
import type { GameConfig } from '../app/GameConfig';
import { getCaseSaveTitle } from '../app/ScenarioCatalog';
import type { ViewportLayout } from '../app/Viewport';
import type { SaveSlotSummary } from '../systems/SaveManager';
import { Button, Modal, Panel, UI_COLORS, UI_FONT_FAMILY } from '../ui';

export type TitleNavigationTarget = 'caseList' | 'gallery' | 'settings' | 'credits';

export interface TitleSceneOptions {
  onStartNewGame?: (slotId: number) => void;
  onLoadGame?: (slotId: number) => void;
  onNavigate?: (target: TitleNavigationTarget) => void;
}

type SlotMode = 'new' | 'load';

interface TitleRainLayerConfig {
  count: number;
  length: number;
  slant: number;
  strokeWidth: number;
  alpha: number;
  speed: number;
  color: number;
  seedX: number;
  seedY: number;
  cycleX: number;
  cycleY: number;
  highlightEvery: number;
  turbulence: number;
  blurAlpha: number;
  opacityJitter: number;
}

interface TitlePortraitConfig {
  id: string;
  x: number;
  y: number;
  scale: number;
  alpha: number;
}

export interface TitleSceneDebugState {
  slotModalVisible: boolean;
  slotMode: SlotMode;
  noticeModalVisible: boolean;
  noticeTitle: string | null;
  titleStatusText: string;
}

const BUTTON_WIDTH = 260;
const BUTTON_HEIGHT = 56;
const SLOT_PANEL_WIDTH = 780;
const SLOT_PANEL_HEIGHT = 520;
const SLOT_ROW_WIDTH = 690;
const SLOT_ROW_HEIGHT = 78;
const SLOT_ROW_GAP = 98;
const TITLE_RAIN_BACK_STREAKS = 210;
const TITLE_RAIN_MID_STREAKS = 132;
const TITLE_RAIN_FRONT_STREAKS = 58;
const TITLE_RAIN_SPLASHES = 76;
const TITLE_RAIN_LENS_DROPS = 24;
const TITLE_NOISE_INTERVAL_MS = 140;
const TITLE_PORTRAIT_MASK_X = 0;
const TITLE_PORTRAIT_MASK_Y = 430;
const TITLE_PORTRAIT_MASK_WIDTH = 720;
const TITLE_PORTRAIT_MASK_HEIGHT = 290;
const TITLE_TAGLINE_PANEL_X = 56;
const TITLE_TAGLINE_PANEL_Y = 344;
const TITLE_TAGLINE_PANEL_WIDTH = 760;
const TITLE_TAGLINE_PANEL_HEIGHT = 108;
const TITLE_TAGLINE_TEXT_X = 132;
const TITLE_TAGLINE_TEXT_Y = 382;
const TITLE_TAGLINE_TEXT_WIDTH = 620;
const TITLE_OPENING_BGM_ID = 'opening_meikacho_no_jikenbo';
const TITLE_OPENING_RESTART_DELAY_MS = 3000;
const TITLE_PORTRAITS: readonly TitlePortraitConfig[] = [
  {
    id: 'tsubakino_akane_title_cutin',
    x: -38,
    y: 404,
    scale: 0.38,
    alpha: 0.98
  },
  {
    id: 'kuremiya_momone_title_cutin',
    x: 292,
    y: 414,
    scale: 0.34,
    alpha: 0.98
  }
];

export class TitleScene extends BaseScene {
  private readonly backgroundLayer = new Container();
  private readonly titlePortraitLayer = new Container();
  private readonly ambientLayer = new Container();
  private readonly rainLayer = new Graphics();
  private readonly noiseLayer = new Graphics();
  private readonly titleLayer = new Container();
  private readonly menuLayer = new Container();
  private readonly modalLayer = new Container();
  private readonly slotModal = new Container();
  private readonly slotOverlay = new Graphics();
  private readonly slotPanel = new Panel({
    width: SLOT_PANEL_WIDTH,
    height: SLOT_PANEL_HEIGHT,
    title: 'セーブスロット'
  });
  private readonly slotHeadingText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 24,
      fontWeight: '800',
      wordWrap: true,
      wordWrapWidth: 690
    }
  });
  private readonly slotHelpText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      lineHeight: 27,
      wordWrap: true,
      wordWrapWidth: 690
    }
  });
  private readonly slotRows = new Container();
  private readonly titleStatusText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      lineHeight: 26,
      wordWrap: true,
      wordWrapWidth: 300
    }
  });
  private readonly continueButton = new Button({
    label: 'つづきから',
    width: BUTTON_WIDTH,
    height: BUTTON_HEIGHT,
    variant: 'secondary',
    onPress: () => this.openSlotModal('load')
  });
  private readonly closeSlotButton = new Button({
    label: '戻る',
    width: 150,
    height: 48,
    variant: 'ghost',
    onPress: () => this.closeSlotModal()
  });

  private layout: ViewportLayout | null = null;
  private noticeModal: Modal | null = null;
  private noticeTitle: string | null = null;
  private currentSlotMode: SlotMode = 'new';
  private titleBackgroundTexture: Texture | null = null;
  private titleLogoTexture: Texture | null = null;
  private titleTaglinePanelTexture: Texture | null = null;
  private readonly titlePortraitTextures = new Map<string, Texture>();
  private backgroundLoadToken = 0;
  private logoLoadToken = 0;
  private taglinePanelLoadToken = 0;
  private portraitLoadToken = 0;
  private ambientElapsedMs = 0;
  private noiseElapsedMs = 0;
  private noiseSeed = 0;
  private titleOpeningPlayToken = 0;
  private titleOpeningRestartTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: TitleSceneOptions = {}
  ) {
    super(services);
  }

  protected override onStart(): void {
    this.container.addChild(this.backgroundLayer);
    this.ambientLayer.addChild(this.rainLayer);
    this.ambientLayer.addChild(this.noiseLayer);
    this.container.addChild(this.ambientLayer);
    this.container.addChild(this.titleLayer);
    this.container.addChild(this.titlePortraitLayer);
    this.container.addChild(this.menuLayer);
    this.container.addChild(this.modalLayer);
    this.createTitle();
    this.createMenu();
    this.createSlotModal();
    this.refreshContinueState();
    this.render();
    void this.loadTitleBackground();
    void this.loadTitleLogo();
    void this.loadTitleTaglinePanel();
    void this.loadTitlePortraits();
    this.playTitleOpeningBgm();
  }

  public override update(ticker: Ticker): void {
    const width = this.layout?.baseWidth ?? this.config.baseResolution.width;
    const height = this.layout?.baseHeight ?? this.config.baseResolution.height;
    const deltaMs = Math.max(0, ticker.deltaMS);

    this.ambientElapsedMs += deltaMs;
    this.noiseElapsedMs += deltaMs;
    if (this.noiseElapsedMs >= TITLE_NOISE_INTERVAL_MS) {
      this.noiseElapsedMs %= TITLE_NOISE_INTERVAL_MS;
      this.noiseSeed = (this.noiseSeed + 1) % 11;
    }

    this.drawAmbient(width, height);
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    this.render();
    this.noticeModal?.setViewportSize(layout.baseWidth, layout.baseHeight);
  }

  public getDebugState(): TitleSceneDebugState {
    return {
      slotModalVisible: this.slotModal.visible,
      slotMode: this.currentSlotMode,
      noticeModalVisible: this.noticeModal !== null,
      noticeTitle: this.noticeTitle,
      titleStatusText: this.titleStatusText.text
    };
  }

  private createTitle(): void {
    this.titleLayer.removeChildren();

    const logo = this.createTitleLogo();
    logo.position.set(54, 42);
    logo.scale.set(0.88);
    this.titleLayer.addChild(logo);

    if (this.titleTaglinePanelTexture) {
      const copyPanel = new Sprite(this.titleTaglinePanelTexture);
      copyPanel.position.set(TITLE_TAGLINE_PANEL_X, TITLE_TAGLINE_PANEL_Y);
      copyPanel.width = TITLE_TAGLINE_PANEL_WIDTH;
      copyPanel.height = TITLE_TAGLINE_PANEL_HEIGHT;
      this.titleLayer.addChild(copyPanel);
    } else {
      const copyPanel = new Graphics();
      copyPanel
        .roundRect(TITLE_TAGLINE_PANEL_X + 10, TITLE_TAGLINE_PANEL_Y + 30, 674, 58, 6)
        .fill({ color: UI_COLORS.black, alpha: 0.76 })
        .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.42 })
        .rect(TITLE_TAGLINE_PANEL_X + 26, TITLE_TAGLINE_PANEL_Y + 39, 5, 38)
        .fill({ color: UI_COLORS.red, alpha: 0.9 });
      this.titleLayer.addChild(copyPanel);
    }

    const copy = new Text({
      text: '毎日事件が起きる街で、今日も真相だけが置き去りになる。',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 21,
        fontWeight: '700',
        lineHeight: 30,
        wordWrap: true,
        wordWrapWidth: TITLE_TAGLINE_TEXT_WIDTH
      }
    });
    copy.position.set(TITLE_TAGLINE_TEXT_X, TITLE_TAGLINE_TEXT_Y);
    this.titleLayer.addChild(copy);

    this.titleStatusText.position.set(82, 606);
    this.titleLayer.addChild(this.titleStatusText);
  }

  private createTitleLogo(): Container {
    const logo = new Container();
    if (this.titleLogoTexture) {
      const sprite = new Sprite(this.titleLogoTexture);
      const scale = Math.min(860 / this.titleLogoTexture.width, 360 / this.titleLogoTexture.height);
      sprite.scale.set(scale);
      logo.addChild(sprite);
      return logo;
    }

    const drawPolygon = (
      target: Graphics,
      points: readonly (readonly [number, number])[]
    ): Graphics => {
      const [first, ...rest] = points;
      target.moveTo(first[0], first[1]);
      for (const [x, y] of rest) {
        target.lineTo(x, y);
      }
      target.lineTo(first[0], first[1]);
      return target;
    };

    const fitText = (text: Text, maxWidth: number): void => {
      if (text.width > maxWidth) {
        const ratio = maxWidth / text.width;
        text.scale.set(ratio);
      }
    };

    const addText = (
      target: Container,
      text: string,
      x: number,
      y: number,
      fontSize: number,
      fill: number,
      alpha = 1,
      rotation = 0,
      maxWidth?: number
    ): Text => {
      const label = new Text({
        text,
        style: {
          fill,
          fontFamily: UI_FONT_FAMILY,
          fontSize,
          fontWeight: '900',
          letterSpacing: 0
        }
      });
      label.position.set(x, y);
      label.alpha = alpha;
      label.rotation = rotation;
      if (maxWidth) {
        fitText(label, maxWidth);
      }
      target.addChild(label);
      return label;
    };

    const addBurstTitle = (
      target: Container,
      text: string,
      x: number,
      y: number,
      fontSize: number,
      maxWidth: number,
      rotation = 0
    ): Text => {
      const outlineOffsets = [
        [-10, 0],
        [10, 0],
        [0, -10],
        [0, 10],
        [-8, -8],
        [8, -8],
        [-8, 8],
        [8, 8],
        [-13, 6],
        [13, -6]
      ] as const;
      const glowOffsets = [
        [16, 18],
        [22, 25],
        [28, 31]
      ] as const;
      const bevelOffsets = [
        [-4, -5],
        [4, 5],
        [0, 7]
      ] as const;

      for (const [offsetX, offsetY] of glowOffsets) {
        addText(
          target,
          text,
          x + offsetX,
          y + offsetY,
          fontSize,
          UI_COLORS.redDark,
          0.92,
          rotation
        );
      }
      for (const [offsetX, offsetY] of outlineOffsets) {
        addText(target, text, x + offsetX, y + offsetY, fontSize, UI_COLORS.black, 0.98, rotation);
      }
      for (const [offsetX, offsetY] of bevelOffsets) {
        addText(target, text, x + offsetX, y + offsetY, fontSize, UI_COLORS.gold, 0.9, rotation);
      }
      const highlight = addText(target, text, x - 3, y - 8, fontSize, 0xffffff, 0.42, rotation);
      highlight.scale.y *= 0.78;
      const main = addText(target, text, x, y, fontSize, 0xfff2cc, 1, rotation);
      fitText(main, maxWidth);
      return main;
    };

    const burst = new Graphics();
    for (let index = 0; index < 22; index += 1) {
      const angle = -0.64 + index * 0.062;
      const startX = 372 + Math.cos(angle) * (118 + (index % 3) * 10);
      const startY = 152 + Math.sin(angle) * (82 + (index % 4) * 8);
      const endX = 372 + Math.cos(angle) * (428 + (index % 5) * 22);
      const endY = 152 + Math.sin(angle) * (230 + (index % 4) * 14);
      burst
        .moveTo(startX, startY)
        .lineTo(endX, endY)
        .stroke({
          color: index % 4 === 0 ? UI_COLORS.gold : index % 4 === 1 ? UI_COLORS.red : 0x7fe8ff,
          width: index % 4 === 0 ? 6 : 3,
          alpha: index % 4 === 2 ? 0.16 : 0.24
        });
    }
    logo.addChild(burst);

    const stickerBase = new Graphics();
    drawPolygon(stickerBase, [
      [34, 92],
      [642, 20],
      [808, 102],
      [760, 234],
      [104, 260],
      [12, 170]
    ])
      .fill({ color: UI_COLORS.black, alpha: 0.72 })
      .stroke({ color: UI_COLORS.black, width: 10, alpha: 0.48 });
    drawPolygon(stickerBase, [
      [44, 82],
      [622, 32],
      [782, 98],
      [728, 210],
      [106, 232],
      [28, 154]
    ])
      .fill({ color: 0xfffbf4, alpha: 0.98 })
      .stroke({ color: 0xff5f73, width: 10, alpha: 0.96 })
      .stroke({ color: UI_COLORS.gold, width: 3, alpha: 0.78 });
    drawPolygon(stickerBase, [
      [76, 108],
      [562, 70],
      [704, 118],
      [620, 166],
      [118, 190],
      [62, 142]
    ]).fill({ color: 0xffefd6, alpha: 0.58 });
    stickerBase
      .circle(76, 90, 14)
      .fill({ color: 0x80e8ff, alpha: 0.88 })
      .circle(112, 64, 8)
      .fill({ color: UI_COLORS.gold, alpha: 0.92 })
      .circle(662, 68, 11)
      .fill({ color: 0xff5f73, alpha: 0.84 })
      .circle(716, 206, 9)
      .fill({ color: 0x80e8ff, alpha: 0.72 })
      .roundRect(118, 210, 84, 10, 5)
      .fill({ color: 0xff5f73, alpha: 0.78 })
      .roundRect(522, 42, 110, 10, 5)
      .fill({ color: 0x80e8ff, alpha: 0.6 });
    logo.addChild(stickerBase);

    const glass = new Graphics();
    glass
      .circle(92, 136, 56)
      .fill({ color: 0x0b1519, alpha: 0.68 })
      .circle(92, 136, 47)
      .stroke({ color: 0xd8f5ff, width: 7, alpha: 0.5 })
      .circle(92, 136, 58)
      .stroke({ color: UI_COLORS.gold, width: 5, alpha: 0.92 })
      .moveTo(52, 180)
      .lineTo(8, 244)
      .stroke({ color: UI_COLORS.gold, width: 15, alpha: 0.9 })
      .moveTo(47, 188)
      .lineTo(4, 252)
      .stroke({ color: UI_COLORS.black, width: 6, alpha: 0.82 })
      .moveTo(67, 106)
      .lineTo(115, 92)
      .stroke({ color: 0xffffff, width: 4, alpha: 0.32 });
    logo.addChild(glass);

    const clock = new Graphics();
    clock
      .circle(682, 92, 62)
      .fill({ color: UI_COLORS.black, alpha: 0.7 })
      .circle(682, 92, 54)
      .stroke({ color: UI_COLORS.gold, width: 4, alpha: 0.8 })
      .circle(682, 92, 31)
      .stroke({ color: 0x7fe8ff, width: 2, alpha: 0.38 });
    for (let index = 0; index < 12; index += 1) {
      const angle = (Math.PI * 2 * index) / 12 - Math.PI / 2;
      const outerX = 682 + Math.cos(angle) * 50;
      const outerY = 92 + Math.sin(angle) * 50;
      const innerX = 682 + Math.cos(angle) * (index % 3 === 0 ? 39 : 44);
      const innerY = 92 + Math.sin(angle) * (index % 3 === 0 ? 39 : 44);
      clock
        .moveTo(innerX, innerY)
        .lineTo(outerX, outerY)
        .stroke({
          color: index % 3 === 0 ? UI_COLORS.red : UI_COLORS.gold,
          width: index % 3 === 0 ? 3 : 1,
          alpha: 0.72
        });
    }
    clock
      .moveTo(682, 92)
      .lineTo(682, 48)
      .stroke({ color: UI_COLORS.red, width: 5, alpha: 0.96 })
      .moveTo(682, 92)
      .lineTo(726, 106)
      .stroke({ color: UI_COLORS.ink, width: 4, alpha: 0.88 })
      .circle(682, 92, 7)
      .fill({ color: UI_COLORS.red, alpha: 0.98 });
    logo.addChild(clock);

    const plate = new Graphics();
    drawPolygon(plate, [
      [58, 96],
      [636, 52],
      [732, 112],
      [690, 186],
      [98, 214],
      [36, 150]
    ])
      .fill({ color: 0xffffff, alpha: 0.18 })
      .stroke({ color: UI_COLORS.black, width: 8, alpha: 0.24 })
      .stroke({ color: 0x80e8ff, width: 3, alpha: 0.44 });
    drawPolygon(plate, [
      [88, 118],
      [548, 82],
      [664, 124],
      [574, 152],
      [112, 176],
      [66, 142]
    ]).fill({ color: 0xff5f73, alpha: 0.1 });
    logo.addChild(plate);

    const lightning = new Graphics();
    drawPolygon(lightning, [
      [28, 190],
      [306, 156],
      [252, 214],
      [760, 132],
      [420, 220],
      [470, 164],
      [80, 240]
    ])
      .fill({ color: 0xff5f73, alpha: 0.64 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.66 });
    lightning
      .moveTo(96, 222)
      .lineTo(702, 142)
      .stroke({ color: UI_COLORS.black, width: 3, alpha: 0.34 })
      .moveTo(332, 198)
      .lineTo(716, 154)
      .stroke({ color: 0x80e8ff, width: 4, alpha: 0.26 });
    logo.addChild(lightning);

    const topRibbon = new Graphics();
    drawPolygon(topRibbon, [
      [214, 2],
      [574, 2],
      [612, 40],
      [184, 48]
    ])
      .fill({ color: 0xfffbf4, alpha: 0.96 })
      .stroke({ color: 0x80e8ff, width: 3, alpha: 0.7 })
      .stroke({ color: 0xff5f73, width: 2, alpha: 0.9 });
    topRibbon.rect(226, 9, 9, 30).fill({ color: UI_COLORS.red, alpha: 0.96 });
    topRibbon.rect(250, 34, 302, 3).fill({ color: 0x80e8ff, alpha: 0.62 });
    logo.addChild(topRibbon);
    addText(logo, 'CASE FILE / NEVER CLOSES', 252, 10, 22, UI_COLORS.redDark, 1, 0, 330);

    addBurstTitle(logo, this.config.projectTitle, 60, 78, 92, 660, -0.018);

    const titleCuts = new Graphics();
    titleCuts
      .moveTo(86, 120)
      .lineTo(610, 78)
      .stroke({ color: 0xffffff, width: 8, alpha: 0.18 })
      .moveTo(140, 172)
      .lineTo(704, 132)
      .stroke({ color: 0xffffff, width: 10, alpha: 0.2 })
      .moveTo(202, 200)
      .lineTo(596, 90)
      .stroke({ color: 0x7fe8ff, width: 3, alpha: 0.32 })
      .moveTo(104, 206)
      .lineTo(684, 186)
      .stroke({ color: UI_COLORS.gold, width: 5, alpha: 0.28 });
    logo.addChild(titleCuts);

    const unresolvedBadge = new Container();
    unresolvedBadge.position.set(582, 164);
    unresolvedBadge.rotation = -0.09;
    const badgeFrame = new Graphics();
    drawPolygon(badgeFrame, [
      [0, 4],
      [160, 0],
      [190, 46],
      [18, 58]
    ])
      .fill({ color: 0xfffbf4, alpha: 0.96 })
      .stroke({ color: UI_COLORS.red, width: 5, alpha: 0.96 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.56 });
    unresolvedBadge.addChild(badgeFrame);
    addText(unresolvedBadge, '未解決', 34, 7, 34, UI_COLORS.redDark, 1, 0, 132);
    logo.addChild(unresolvedBadge);

    const subtitlePanel = new Graphics();
    drawPolygon(subtitlePanel, [
      [76, 246],
      [694, 230],
      [742, 274],
      [118, 304],
      [54, 270]
    ])
      .fill({ color: 0xfffbf4, alpha: 0.96 })
      .stroke({ color: UI_COLORS.gold, width: 5, alpha: 0.86 })
      .stroke({ color: 0xff5f73, width: 3, alpha: 0.92 });
    subtitlePanel
      .moveTo(98, 298)
      .lineTo(654, 272)
      .stroke({ color: 0x80e8ff, width: 4, alpha: 0.56 });
    logo.addChild(subtitlePanel);

    addText(
      logo,
      `~ ${this.config.projectSubtitle} ~`,
      106,
      246,
      30,
      UI_COLORS.black,
      1,
      -0.015,
      600
    );

    return logo;
  }

  private createMenu(): void {
    this.menuLayer.removeChildren();

    const buttons = [
      new Button({
        label: 'はじめから',
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        variant: 'primary',
        onPress: () => this.openSlotModal('new')
      }),
      this.continueButton,
      new Button({
        label: '事件リスト',
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        variant: 'secondary',
        onPress: () => this.handleNavigate('caseList')
      }),
      new Button({
        label: 'ギャラリー',
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        variant: 'secondary',
        onPress: () => this.handleNavigate('gallery')
      }),
      new Button({
        label: '設定',
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        variant: 'secondary',
        onPress: () => this.handleNavigate('settings')
      }),
      new Button({
        label: 'クレジット',
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        variant: 'ghost',
        onPress: () => this.handleNavigate('credits')
      })
    ];

    for (const [index, button] of buttons.entries()) {
      button.position.set(0, index * 70);
      this.menuLayer.addChild(button);
    }
  }

  private createSlotModal(): void {
    this.slotModal.visible = false;
    this.slotOverlay.eventMode = 'static';
    this.slotOverlay.cursor = 'default';
    this.slotOverlay.on('pointertap', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      this.closeSlotModal();
    });

    this.slotPanel.eventMode = 'static';
    this.slotPanel.cursor = 'default';
    this.slotPanel.on('pointertap', (event: FederatedPointerEvent) => event.stopPropagation());

    this.slotPanel.content.addChild(this.slotHeadingText);
    this.slotPanel.content.addChild(this.slotHelpText);
    this.slotPanel.content.addChild(this.slotRows);
    this.slotPanel.content.addChild(this.closeSlotButton);
    this.slotModal.addChild(this.slotOverlay);
    this.slotModal.addChild(this.slotPanel);
    this.modalLayer.addChild(this.slotModal);
  }

  private render(): void {
    const width = this.layout?.baseWidth ?? this.config.baseResolution.width;
    const height = this.layout?.baseHeight ?? this.config.baseResolution.height;

    this.drawBackground(width, height);
    this.drawTitlePortraits(width, height);
    this.drawAmbient(width, height);
    this.menuLayer.position.set(width - 370, 186);
    this.titleStatusText.position.set(width - 370, 612);
    this.renderSlotModal(width, height);
  }

  private drawBackground(width: number, height: number): void {
    this.backgroundLayer.removeChildren();

    const base = new Graphics();
    base.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.backgroundLayer.addChild(base);

    if (this.titleBackgroundTexture) {
      const sprite = new Sprite(this.titleBackgroundTexture);
      coverSprite(sprite, width, height);
      this.backgroundLayer.addChild(sprite);
    } else {
      base.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.22 });
    }

    const readabilityOverlay = new Graphics();
    readabilityOverlay.rect(0, 0, width, height).fill({ color: UI_COLORS.black, alpha: 0.34 });
    readabilityOverlay.rect(0, 0, Math.min(width, 760), height).fill({
      color: UI_COLORS.black,
      alpha: 0.28
    });
    readabilityOverlay.rect(0, height - 70, width, 70).fill({
      color: UI_COLORS.black,
      alpha: 0.36
    });
    readabilityOverlay.rect(0, 0, width, 9).fill({ color: UI_COLORS.red, alpha: 0.86 });
    readabilityOverlay.rect(0, height - 9, width, 9).fill({ color: UI_COLORS.gold, alpha: 0.78 });
    this.backgroundLayer.addChild(readabilityOverlay);
  }

  private drawTitlePortraits(width: number, height: number): void {
    this.titlePortraitLayer.removeChildren();
    if (this.titlePortraitTextures.size === 0) {
      return;
    }

    const frame = new Graphics();
    frame
      .rect(
        TITLE_PORTRAIT_MASK_X,
        TITLE_PORTRAIT_MASK_Y,
        TITLE_PORTRAIT_MASK_WIDTH,
        TITLE_PORTRAIT_MASK_HEIGHT
      )
      .fill({ color: UI_COLORS.black, alpha: 0.08 })
      .rect(TITLE_PORTRAIT_MASK_X, TITLE_PORTRAIT_MASK_Y, 8, TITLE_PORTRAIT_MASK_HEIGHT)
      .fill({ color: UI_COLORS.red, alpha: 0.52 })
      .rect(
        TITLE_PORTRAIT_MASK_X + 8,
        TITLE_PORTRAIT_MASK_Y + TITLE_PORTRAIT_MASK_HEIGHT - 8,
        TITLE_PORTRAIT_MASK_WIDTH - 8,
        2
      )
      .fill({ color: UI_COLORS.gold, alpha: 0.38 });
    this.titlePortraitLayer.addChild(frame);

    const portraitContainer = new Container();
    const mask = new Graphics();
    mask
      .rect(
        TITLE_PORTRAIT_MASK_X,
        TITLE_PORTRAIT_MASK_Y,
        Math.min(TITLE_PORTRAIT_MASK_WIDTH, width),
        Math.min(TITLE_PORTRAIT_MASK_HEIGHT, height - TITLE_PORTRAIT_MASK_Y)
      )
      .fill(UI_COLORS.ink);
    portraitContainer.mask = mask;
    this.titlePortraitLayer.addChild(portraitContainer);
    this.titlePortraitLayer.addChild(mask);

    for (const portrait of TITLE_PORTRAITS) {
      const texture = this.titlePortraitTextures.get(portrait.id);
      if (!texture) {
        continue;
      }

      const shadow = new Sprite(texture);
      shadow.position.set(portrait.x + 10, portrait.y + 12);
      shadow.scale.set(portrait.scale);
      shadow.tint = UI_COLORS.black;
      shadow.alpha = 0.42;
      portraitContainer.addChild(shadow);

      const sprite = new Sprite(texture);
      sprite.position.set(portrait.x, portrait.y);
      sprite.scale.set(portrait.scale);
      sprite.alpha = portrait.alpha;
      portraitContainer.addChild(sprite);
    }

    const vignette = new Graphics();
    vignette
      .rect(
        TITLE_PORTRAIT_MASK_X,
        TITLE_PORTRAIT_MASK_Y,
        TITLE_PORTRAIT_MASK_WIDTH,
        TITLE_PORTRAIT_MASK_HEIGHT
      )
      .fill({ color: UI_COLORS.black, alpha: 0.08 })
      .rect(0, TITLE_PORTRAIT_MASK_Y, 82, TITLE_PORTRAIT_MASK_HEIGHT)
      .fill({ color: UI_COLORS.black, alpha: 0.16 })
      .rect(0, height - 54, TITLE_PORTRAIT_MASK_WIDTH, 54)
      .fill({ color: UI_COLORS.black, alpha: 0.34 });
    this.titlePortraitLayer.addChild(vignette);
  }

  private drawAmbient(width: number, height: number): void {
    this.rainLayer.clear();
    this.noiseLayer.clear();

    this.drawTitleRain(width, height);
    this.drawTitleNoise(width, height);
  }

  private drawTitleNoise(width: number, height: number): void {
    const scanlineAlpha = 0.045 + (this.noiseSeed % 3) * 0.01;
    for (let y = 28; y < height; y += 42) {
      this.noiseLayer.rect(0, y, width, 1).fill({ color: UI_COLORS.ink, alpha: scanlineAlpha });
    }

    for (let index = 0; index < 42; index += 1) {
      const x = (index * 97 + this.noiseSeed * 53 + 13) % width;
      const y = (index * 59 + this.noiseSeed * 31 + 23) % height;
      const isWarm = index % 5 === 0;
      this.noiseLayer.rect(x, y, 2 + (index % 3), 1).fill({
        color: isWarm ? UI_COLORS.gold : UI_COLORS.ink,
        alpha: isWarm ? 0.12 : 0.075
      });
    }
  }

  private drawTitleRain(width: number, height: number): void {
    const wind = Math.sin(this.ambientElapsedMs * 0.00042) * 26;
    this.drawTitleRainVeil(width, height, wind);
    this.drawTitleRainLayer(
      width,
      height,
      {
        count: TITLE_RAIN_BACK_STREAKS,
        length: 52,
        slant: 22,
        strokeWidth: 1,
        alpha: 0.095,
        speed: 0.18,
        color: 0x7895aa,
        seedX: 67,
        seedY: 109,
        cycleX: 220,
        cycleY: 420,
        highlightEvery: 23,
        turbulence: 14,
        blurAlpha: 0.12,
        opacityJitter: 0.3
      },
      wind
    );
    this.drawTitleRainLayer(
      width,
      height,
      {
        count: TITLE_RAIN_MID_STREAKS,
        length: 94,
        slant: 42,
        strokeWidth: 1.35,
        alpha: 0.19,
        speed: 0.29,
        color: 0xb8d0e3,
        seedX: 83,
        seedY: 137,
        cycleX: 260,
        cycleY: 520,
        highlightEvery: 11,
        turbulence: 22,
        blurAlpha: 0.22,
        opacityJitter: 0.42
      },
      wind
    );
    this.drawTitleRainLayer(
      width,
      height,
      {
        count: TITLE_RAIN_FRONT_STREAKS,
        length: 158,
        slant: 72,
        strokeWidth: 2.35,
        alpha: 0.34,
        speed: 0.45,
        color: 0xe7f6ff,
        seedX: 127,
        seedY: 71,
        cycleX: 320,
        cycleY: 660,
        highlightEvery: 4,
        turbulence: 36,
        blurAlpha: 0.34,
        opacityJitter: 0.55
      },
      wind
    );

    this.drawTitleGroundMist(width, height);
    this.drawTitleRainSplashes(width, height, wind);
    this.drawTitleLensDrops(width, height, wind);
  }

  private drawTitleRainVeil(width: number, height: number, wind: number): void {
    const drift = (this.ambientElapsedMs * 0.018) % 180;
    for (let index = 0; index < 9; index += 1) {
      const y = height * (0.12 + index * 0.095) + ((drift + index * 37) % 96) - 48;
      const thickness = 18 + (index % 3) * 10;
      const alpha = 0.018 + (index % 4) * 0.006;
      this.rainLayer
        .moveTo(width + 210, y - wind * 0.32)
        .lineTo(-240, y + 92 + wind * 0.22)
        .stroke({
          color: index % 2 === 0 ? 0x8fb2c8 : 0xd4e8f5,
          width: thickness,
          alpha
        });
    }
    for (let index = 0; index < 5; index += 1) {
      const x = ((index * 271 + this.ambientElapsedMs * 0.012) % (width + 240)) - 120;
      this.rainLayer.rect(x, 0, 76 + index * 18, height).fill({
        color: index % 2 === 0 ? 0xb8d0e3 : 0xffffff,
        alpha: 0.012 + index * 0.002
      });
    }
  }

  private drawTitleGroundMist(width: number, height: number): void {
    const mistOffset = (this.ambientElapsedMs * 0.016) % 34;
    for (let index = 0; index < 6; index += 1) {
      const y = height * (0.43 + index * 0.09) + ((index * 11 + mistOffset) % 22);
      this.rainLayer.rect(0, y, width, 11 + index * 4).fill({
        color: index % 2 === 0 ? 0x8db4ca : 0xdcecff,
        alpha: 0.024 + index * 0.005
      });
    }
    this.rainLayer.rect(0, height - 92, width, 78).fill({
      color: 0xaac8dc,
      alpha: 0.026
    });
  }

  private drawTitleRainSplashes(width: number, height: number, wind: number): void {
    const splashFrame = Math.floor(this.ambientElapsedMs / 64) % 13;
    for (let index = 0; index < TITLE_RAIN_SPLASHES; index += 1) {
      const baseX = ((index * 113 + 29 + splashFrame * 17 + wind * 0.32) % (width + 80)) - 40;
      const baseY = height - 26 - ((index * 19 + splashFrame) % 54);
      const spread = 5 + (index % 6) * 3.2;
      const alpha = 0.048 + (index % 5) * 0.014;
      this.rainLayer
        .moveTo(baseX - spread, baseY)
        .lineTo(baseX + spread, baseY - 2)
        .stroke({ color: 0xdcecff, width: 1, alpha });
      this.rainLayer
        .moveTo(baseX - spread * 0.5, baseY + 3)
        .lineTo(baseX + spread * 0.72, baseY + 1)
        .stroke({ color: 0x9fbcd0, width: 1, alpha: alpha * 0.48 });
      if (index % 3 === 0) {
        this.rainLayer
          .moveTo(baseX, baseY)
          .lineTo(baseX - spread * 0.45, baseY - 7)
          .stroke({ color: 0xf4fbff, width: 1, alpha: alpha * 0.72 });
      }
      if (index % 4 === 0) {
        this.rainLayer.circle(baseX + spread * 0.4, baseY - 4, 1.1).fill({
          color: 0xf4fbff,
          alpha: alpha * 0.54
        });
      }
    }
  }

  private drawTitleLensDrops(width: number, height: number, wind: number): void {
    for (let index = 0; index < TITLE_RAIN_LENS_DROPS; index += 1) {
      const fall = (this.ambientElapsedMs * (0.012 + (index % 5) * 0.004)) % (height + 90);
      const wobble = Math.sin(this.ambientElapsedMs * 0.0012 + index * 0.91) * (3 + (index % 4));
      const x = (index * 173 + 37 + wobble + wind * 0.08) % width;
      const y = ((index * 71 + fall) % (height + 90)) - 45;
      const radius = 1.3 + (index % 4) * 0.55;
      const alpha = 0.025 + (index % 5) * 0.006;
      this.rainLayer.circle(x, y, radius).fill({ color: 0xe8f6ff, alpha });
      this.rainLayer
        .moveTo(x + radius * 0.6, y + radius)
        .lineTo(x + radius * 0.2, y + 14 + radius * 2)
        .stroke({ color: 0xd7ecff, width: 1, alpha: alpha * 0.62 });
      if (index % 3 === 0) {
        this.rainLayer.circle(x - radius * 0.45, y - radius * 0.35, 0.65).fill({
          color: 0xffffff,
          alpha: alpha * 1.6
        });
      }
    }
  }

  private drawTitleRainLayer(
    width: number,
    height: number,
    config: TitleRainLayerConfig,
    wind: number
  ): void {
    const fieldWidth = width + 640;
    const fieldHeight = height + config.cycleY + 360;
    const localGust =
      Math.sin(this.ambientElapsedMs * 0.00072 + config.seedX) * config.turbulence +
      Math.sin(this.ambientElapsedMs * 0.0018 + config.seedY) * config.turbulence * 0.42;
    const layerWind = wind + localGust;
    const offsetX = (this.ambientElapsedMs * config.speed * 0.24 + layerWind * 5) % config.cycleX;
    const offsetY = (this.ambientElapsedMs * config.speed) % fieldHeight;

    for (let index = 0; index < config.count; index += 1) {
      const depth = ((index * 31 + config.seedX) % 100) / 100;
      let x =
        ((index * config.seedX + 41 + offsetX) % fieldWidth) -
        300 +
        layerWind * (0.18 + depth * 0.1);
      if (x > width + 280) {
        x -= fieldWidth;
      }
      if (x < -320) {
        x += fieldWidth;
      }
      const y = ((index * config.seedY + 17 + offsetY) % fieldHeight) - config.cycleY - 150;
      const shimmer = Math.sin(this.ambientElapsedMs * 0.0011 + index * 0.73);
      const length = config.length * (0.68 + depth * 0.82) * (0.92 + shimmer * 0.08);
      const slant = config.slant * (0.82 + depth * 0.42) + layerWind * (0.28 + depth * 0.12);
      const alpha =
        config.alpha *
        (0.58 + depth * 0.54) *
        (1 + shimmer * config.opacityJitter * 0.18) *
        (index % config.highlightEvery === 0 ? 1.2 : 1);

      if (config.blurAlpha > 0) {
        this.rainLayer
          .moveTo(x + 4, y - 14)
          .lineTo(x - slant + 4, y + length + 12)
          .stroke({
            color: config.color,
            width: config.strokeWidth + 2.4,
            alpha: alpha * config.blurAlpha
          });
      }
      this.rainLayer
        .moveTo(x, y)
        .lineTo(x - slant, y + length)
        .stroke({ color: config.color, width: config.strokeWidth, alpha });

      if (index % config.highlightEvery === 0) {
        this.rainLayer
          .moveTo(x + 2, y - 10)
          .lineTo(x - slant * 0.58 + 2, y + length * 0.52)
          .stroke({
            color: 0xffffff,
            width: Math.max(1, config.strokeWidth * 0.7),
            alpha: alpha * 0.38
          });
      }
    }
  }

  private async loadTitleBackground(): Promise<void> {
    const token = ++this.backgroundLoadToken;
    const image = await this.services.getAssets().loadImage({
      id: 'title_background',
      kind: 'background'
    });

    if (token !== this.backgroundLoadToken || image.placeholder) {
      return;
    }

    this.titleBackgroundTexture = image.texture;
    this.render();
  }

  private async loadTitleLogo(): Promise<void> {
    const token = ++this.logoLoadToken;
    const image = await this.services.getAssets().loadImage({
      id: 'title_logo',
      kind: 'ui'
    });

    if (token !== this.logoLoadToken || image.placeholder) {
      return;
    }

    this.titleLogoTexture = image.texture;
    this.createTitle();
    this.render();
  }

  private async loadTitleTaglinePanel(): Promise<void> {
    const token = ++this.taglinePanelLoadToken;
    const image = await this.services.getAssets().loadImage({
      id: 'title_tagline_panel',
      kind: 'ui'
    });

    if (token !== this.taglinePanelLoadToken || image.placeholder) {
      return;
    }

    this.titleTaglinePanelTexture = image.texture;
    this.createTitle();
    this.render();
  }

  private async loadTitlePortraits(): Promise<void> {
    const token = ++this.portraitLoadToken;
    const images = await Promise.all(
      TITLE_PORTRAITS.map((portrait) =>
        this.services.getAssets().loadImage({
          id: portrait.id,
          kind: 'character'
        })
      )
    );

    if (token !== this.portraitLoadToken) {
      return;
    }

    for (const image of images) {
      if (!image.placeholder) {
        this.titlePortraitTextures.set(image.id, image.texture);
      }
    }
    this.render();
  }

  protected override onDestroy(): void {
    this.backgroundLoadToken += 1;
    this.logoLoadToken += 1;
    this.taglinePanelLoadToken += 1;
    this.portraitLoadToken += 1;
    this.titleOpeningPlayToken += 1;
    this.clearTitleOpeningRestartTimer();
    this.titleBackgroundTexture = null;
    this.titleLogoTexture = null;
    this.titleTaglinePanelTexture = null;
    this.titlePortraitTextures.clear();
    void this.services.getAudio().stopBgm(0);
  }

  private playTitleOpeningBgm(): void {
    const token = ++this.titleOpeningPlayToken;
    this.clearTitleOpeningRestartTimer();
    void this.services.getAudio().playBgm(TITLE_OPENING_BGM_ID, {
      fadeMs: 0,
      loop: false,
      onEnd: () => this.scheduleTitleOpeningReplay(token)
    });
  }

  private scheduleTitleOpeningReplay(token: number): void {
    if (token !== this.titleOpeningPlayToken) {
      return;
    }

    this.clearTitleOpeningRestartTimer();
    this.titleOpeningRestartTimer = globalThis.setTimeout(() => {
      if (token !== this.titleOpeningPlayToken) {
        return;
      }

      this.playTitleOpeningBgm();
    }, TITLE_OPENING_RESTART_DELAY_MS);
  }

  private clearTitleOpeningRestartTimer(): void {
    if (this.titleOpeningRestartTimer === null) {
      return;
    }

    globalThis.clearTimeout(this.titleOpeningRestartTimer);
    this.titleOpeningRestartTimer = null;
  }

  private openSlotModal(mode: SlotMode): void {
    this.currentSlotMode = mode;
    this.slotModal.visible = true;
    this.refreshSlotRows();
  }

  private closeSlotModal(): void {
    this.slotModal.visible = false;
  }

  private renderSlotModal(width: number, height: number): void {
    this.slotOverlay.clear();
    this.slotOverlay.rect(0, 0, width, height).fill({ color: UI_COLORS.black, alpha: 0.68 });
    this.slotPanel.position.set((width - SLOT_PANEL_WIDTH) / 2, (height - SLOT_PANEL_HEIGHT) / 2);
    this.closeSlotButton.position.set(SLOT_PANEL_WIDTH - 206, SLOT_PANEL_HEIGHT - 150);
  }

  private refreshSlotRows(): void {
    this.slotRows.removeChildren();
    this.slotHeadingText.text =
      this.currentSlotMode === 'new' ? '保存先を選択' : '読み込むデータを選択';
    this.slotHelpText.text =
      this.currentSlotMode === 'new'
        ? '空きスロットはそのまま開始できます。既存/破損/旧形式データは確認後に上書きします。'
        : '有効なセーブデータだけを読み込めます。破損/旧形式データと設定値は削除されません。';
    this.slotHelpText.position.set(0, 42);
    this.slotRows.position.set(0, 94);

    const slots = this.services.getSaveManager().listSlots();
    for (const [index, slot] of slots.entries()) {
      this.slotRows.addChild(this.createSlotRow(slot, index));
    }
  }

  private createSlotRow(slot: SaveSlotSummary, index: number): Container {
    const row = new Container();
    const y = index * SLOT_ROW_GAP;
    const background = new Graphics();
    background
      .roundRect(0, y, SLOT_ROW_WIDTH, SLOT_ROW_HEIGHT, 6)
      .fill({ color: UI_COLORS.paperLight, alpha: 0.86 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.42 });
    row.addChild(background);

    const label = new Text({
      text: `Slot ${slot.slotId}`,
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 19,
        fontWeight: '800'
      }
    });
    label.position.set(20, y + 13);
    row.addChild(label);

    const detail = new Text({
      text: getSlotDetail(slot),
      style: {
        fill: slot.status === 'valid' ? UI_COLORS.ink : UI_COLORS.mutedInk,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 17,
        lineHeight: 25,
        wordWrap: true,
        wordWrapWidth: 354
      }
    });
    detail.position.set(20, y + 39);
    row.addChild(detail);

    const isLoadDisabled = this.currentSlotMode === 'load' && slot.status !== 'valid';
    const action = new Button({
      label: this.currentSlotMode === 'new' ? '選択' : 'ロード',
      width: 112,
      height: 48,
      variant: this.currentSlotMode === 'new' ? 'primary' : 'secondary',
      disabled: isLoadDisabled,
      onPress: () => this.handleSlotAction(slot)
    });
    action.position.set(444, y + 15);
    row.addChild(action);

    const deleteButton = new Button({
      label: '削除',
      width: 104,
      height: 48,
      variant: 'danger',
      disabled: slot.status !== 'valid',
      onPress: () => this.confirmDeleteSlot(slot)
    });
    deleteButton.position.set(574, y + 15);
    row.addChild(deleteButton);

    return row;
  }

  private handleSlotAction(slot: SaveSlotSummary): void {
    if (this.currentSlotMode === 'load') {
      this.loadGame(slot.slotId);
      return;
    }

    if (slot.status === 'empty') {
      this.startNewGame(slot.slotId);
      return;
    }

    this.showConfirm(
      '上書き確認',
      `Slot ${slot.slotId} のデータを上書きして最初から始めます。よろしいですか？\n\n音量やテキスト速度などの設定は保持されます。`,
      '上書き',
      () => this.startNewGame(slot.slotId)
    );
  }

  private confirmDeleteSlot(slot: SaveSlotSummary): void {
    if (slot.status !== 'valid') {
      return;
    }

    this.showConfirm(
      '削除確認',
      `Slot ${slot.slotId} の保存データを削除します。\n\n音量やテキスト速度などの設定は削除されません。`,
      '削除',
      () => this.deleteSlot(slot.slotId)
    );
  }

  private deleteSlot(slotId: number): void {
    this.services.getSaveManager().deleteSlot(slotId);
    this.refreshSlotRows();
    this.refreshContinueState(`Slot ${slotId} の保存データを削除しました。`);
  }

  private startNewGame(slotId: number): void {
    const state = this.services.getState();
    state.resetProgress(this.config);
    state.setCurrentCase(this.config.initialCaseId, 'investigating');
    this.services
      .getSaveManager()
      .saveSlot(slotId, state.getSnapshot(), getCaseSaveTitle(this.config.initialCaseId));
    this.closeSlotModal();
    this.refreshContinueState(`Slot ${slotId} に事件ファイルを作成しました。`);
    this.showNotice('事件ファイル作成', 'Case 0「消えた宝石商の娘」を開始できます。');
    this.options.onStartNewGame?.(slotId);
  }

  private loadGame(slotId: number): void {
    const result = this.services.getSaveManager().loadSlot(slotId);
    if (result.status !== 'valid' || !result.record) {
      this.showNotice('ロード不可', getLoadErrorMessage(result.status));
      this.refreshSlotRows();
      return;
    }

    this.services.getState().hydrate(result.record.snapshot);
    this.closeSlotModal();
    this.refreshContinueState(`Slot ${slotId} を読み込みました。`);
    this.showNotice('ロード完了', `${result.record.title} を読み込みました。`);
    this.options.onLoadGame?.(slotId);
  }

  private handleNavigate(target: TitleNavigationTarget): void {
    if (this.options.onNavigate) {
      this.options.onNavigate(target);
      return;
    }

    if (target === 'credits') {
      this.showNotice(
        'クレジット',
        `${this.config.projectFullTitle}\n\n企画・開発: nun-game\n使用技術: Vite / TypeScript / PixiJS / Howler.js\n\n本作は独自のキャラクター・舞台で制作するギャグミステリーADVです。`
      );
      return;
    }

    const label = target === 'caseList' ? '事件リスト' : '設定';
    const message =
      target === 'caseList'
        ? '資料棚は鍵がかかっています。警部がまた鍵をなくしました。'
        : '調整用のつまみはあるのに、誰も正しい目盛りを覚えていません。';
    this.showNotice(label, message);
  }

  private refreshContinueState(statusMessage?: string): void {
    const slots = this.services.getSaveManager().listSlots();
    const hasAnySave = slots.some((slot) => slot.status === 'valid');
    this.continueButton.setDisabled(!hasAnySave);
    this.titleStatusText.text = statusMessage ?? getTitleSaveStatusMessage(slots);
  }

  private showConfirm(
    title: string,
    message: string,
    confirmLabel: string,
    onConfirm: () => void
  ): void {
    this.destroyNoticeModal();
    this.noticeTitle = title;
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '戻る',
          variant: 'ghost',
          onPress: () => this.destroyNoticeModal()
        },
        {
          label: confirmLabel,
          variant: 'danger',
          onPress: () => {
            this.destroyNoticeModal();
            onConfirm();
          }
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private showNotice(title: string, message: string): void {
    this.destroyNoticeModal();
    this.noticeTitle = title;
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '閉じる',
          variant: 'primary',
          onPress: () => this.destroyNoticeModal()
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private destroyNoticeModal(): void {
    if (!this.noticeModal) {
      return;
    }

    this.noticeModal.destroy({ children: true });
    this.noticeModal = null;
    this.noticeTitle = null;
  }
}

function getSlotDetail(slot: SaveSlotSummary): string {
  switch (slot.status) {
    case 'empty':
      return '空きスロット';
    case 'valid':
      return `${slot.title ?? '保存データ'}\n${formatSavedAt(slot.savedAt)}`;
    case 'corrupt':
      return `破損データ\n${slot.reason ?? '読み込めません'}`;
    case 'versionMismatch':
      return `旧形式データ\n${slot.reason ?? '現在のバージョンでは読み込めません'}`;
  }
}

function getLoadErrorMessage(status: SaveSlotSummary['status']): string {
  switch (status) {
    case 'empty':
      return 'このスロットには保存データがありません。';
    case 'corrupt':
      return '保存データが破損しているため読み込めません。';
    case 'versionMismatch':
      return '保存データの形式が異なるため読み込めません。';
    case 'valid':
      return '保存データを読み込めませんでした。';
  }
}

function getTitleSaveStatusMessage(slots: SaveSlotSummary[]): string {
  const hasValidSave = slots.some((slot) => slot.status === 'valid');
  const hasUnreadableSave = slots.some(
    (slot) => slot.status === 'corrupt' || slot.status === 'versionMismatch'
  );

  if (hasValidSave && hasUnreadableSave) {
    return '保存済みの事件ファイルがあります。一部の保存データは読み込めません。';
  }

  if (hasValidSave) {
    return '保存済みの事件ファイルがあります。';
  }

  if (hasUnreadableSave) {
    return '読み込めない事件ファイルがあります。はじめからで保存先を選ぶと復旧できます。';
  }

  return '保存済みの事件ファイルはありません。';
}

function formatSavedAt(value: string | null): string {
  if (!value) {
    return '保存日時なし';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
}

function coverSprite(sprite: Sprite, width: number, height: number): void {
  const textureWidth = sprite.texture.width || width;
  const textureHeight = sprite.texture.height || height;
  const scale = Math.max(width / textureWidth, height / textureHeight);

  sprite.scale.set(scale);
  sprite.position.set(
    Math.round((width - textureWidth * scale) / 2),
    Math.round((height - textureHeight * scale) / 2)
  );
}
